import MESSAGE_TYPES from './constants/messageTypes';
import DeltaHelper, {DeviceInfoEvent} from '@/services/delta/DeltaHelper';
import '../types/window';
import Sentry from '@/services/sentry/Sentry';
import getLtid from './getLtid';
import EmberIframe from '@/components/ember-iframe';
import Fortress from '@/services/fortress/Fortress';

type MessageHandler = (payload: any) => void;

const messageHandlers: Record<string, MessageHandler[]> = {};

/**
 * Initialize the webview message handler
 * This sets up an event listener for messages from the React Native webview
 */
export function initWebviewMessageHandler() {
  window.addEventListener('message', handleWebviewMessage, false);
  registerMessageHandler(MESSAGE_TYPES.AD_INFO, handleAdInfoMessage);
  registerMessageHandler(MESSAGE_TYPES.LOGOUT, handleLogoutMessage);
}

/**
 * Register a handler for a specific message type
 * @param messageType The type of message to handle
 * @param handler The function to call when a message of this type is received
 */
export function registerMessageHandler(
  messageType: string,
  handler: MessageHandler
) {
  if (!messageHandlers[messageType]) {
    messageHandlers[messageType] = [];
  }

  messageHandlers[messageType].push(handler);
}

/**
 * Remove a handler for a specific message type
 * @param messageType The type of message
 * @param handler The handler function to remove
 */
export function unregisterMessageHandler(
  messageType: string,
  handler: MessageHandler
) {
  if (!messageHandlers[messageType]) {
    return;
  }

  const index = messageHandlers[messageType].indexOf(handler);
  if (index !== -1) {
    messageHandlers[messageType].splice(index, 1);
  }
}

/**
 * Handle messages from the React Native webview
 */
function handleWebviewMessage(event: MessageEvent) {
  // Only accept messages from our own origin
  if (event.origin !== window.location.origin) {
    return;
  }

  let data: {type: string; payload: any};
  try {
    // Parse the message data if it's a string
    data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
  } catch (e) {
    Sentry.captureException({
      message: 'Error parsing message data from React Native',
      data: event.data,
    });
    return;
  }

  const messageType = data?.type;
  if (!messageType) {
    return;
  }

  // Call all registered handlers for this message type
  const handlers = messageHandlers[messageType] || [];
  handlers.forEach((handler) => {
    try {
      handler(data.payload);
    } catch (e) {
      Sentry.captureException({
        message: `Error in handler for React Native message type ${messageType}`,
        data: data.payload,
        error: e,
      });
    }
  });
}

/**
 * Handle device info messages from the React Native webview
 */
function handleAdInfoMessage(adInfo: {
  appsFlyerId?: string;
  advertisingId?: string;
  platform?: string;
  appName?: string;
  deviceCountry?: string;
  osVersion?: string;
  bundleId?: string;
  platformDeviceId?: string;
}) {
  if (window.PP_APP_INFO) {
    if (adInfo.appsFlyerId) {
      window.PP_APP_INFO.appsFlyerId = adInfo.appsFlyerId;
    }
    if (adInfo.advertisingId) {
      window.PP_APP_INFO.advertisingId = adInfo.advertisingId;
    }
    if (adInfo.platform) {
      window.PP_APP_INFO.platform = adInfo.platform;
    }
    if (adInfo.appName) {
      window.PP_APP_INFO.app_name = adInfo.appName;
    }
    if (adInfo.deviceCountry) {
      window.PP_APP_INFO.device_country = adInfo.deviceCountry;
    }
    if (adInfo.osVersion) {
      window.PP_APP_INFO.os_version = adInfo.osVersion;
    }
    if (adInfo.bundleId) {
      window.PP_APP_INFO.app_bundle_id = adInfo.bundleId;
    }
    if (adInfo.platformDeviceId) {
      window.PP_APP_INFO.platform_device_id = adInfo.platformDeviceId;
    }
  }

  const payload: Partial<DeviceInfoEvent> = {
    appsflyer_id: adInfo.appsFlyerId,
    advertising_id: adInfo.advertisingId,
    platform: adInfo.platform,
    lt_id: getLtid(),
    app_name: adInfo.appName,
    device_country: adInfo.deviceCountry,
    os_version: adInfo.osVersion,
    app_bundle_id: adInfo.bundleId,
    platform_device_id: adInfo.platformDeviceId,
  };

  DeltaHelper.saveAndSendDeviceInfoLog(payload);
}

function handleLogoutMessage() {
  EmberIframe.send('logOut', null);
  setTimeout(() => {
    Fortress.clearCookieTokens();
    // Refresh The page on logout so that react-query will delete the old user data.
    window.location.reload();
  });
}

/**
 * Clean up function to remove event listeners
 */
export function cleanupWebviewMessageHandler() {
  window.removeEventListener('message', handleWebviewMessage, false);

  Object.keys(messageHandlers).forEach((key) => {
    messageHandlers[key] = [];
  });
}
